<template>
	<PageContainer>
		<VCard class="d-flex flex-column pa-12">
			<p
				class="text-h4 text-md-h2 pb-3"
				style="text-align: center"
			>
				Trouver un psychologue conventionné avec l’Assurance Maladie
			</p>

			<!-- Champ de recherche -->
			<SearchSection
				@on-change-psychologist-list="handleChangePsychologistList"
				@on-change-city="handleChangeCity"
				@is-search-done="handleSearchDone"
			/>

			<!-- Liste des psychologues et carte -->
			<div
				v-if="psychologists.length"
				id="result-container"
				class="result-container"
			>
				<PsychologistList
					:selected-psychologist.sync="selectedPsychologistId"
					:psychologist-list="psychologists"
					@selected-psychologist-id="handleChangePsychologistId"
				/>

				<PsychologistMap
					v-if="psychologists.length && city"
					:city="city"
					:psychologist-id-by-address="selectedPsychologistId"
					:psychologists="psychologists"
					@on-change-psychologist="handleChangePsychologistId"
				/>
			</div>

			<!-- Alerte -->
			<AlertWrapper
				v-if="displayNotFoundAlert"
				outlined
				type="info"
			>
				<h1>Pas encore de psychologues partenaires dans cette zone</h1>
				<p>
					Nous mettons à jour cette liste régulièrement. N'hésitez pas à dézoomer sur la carte (cliquer sur -)
					pour voir les psychologues proches de chez vous. A noter, certains psychologues acceptent les
					séances à distance après la première rencontre physique
				</p>
			</AlertWrapper>

			<v-divider style="margin-top: 40px;" />

			<!-- Section "A retenir" -->
			<RememberSection />
		</VCard>
	</PageContainer>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component from 'vue-class-component';
	import { Meta, MetaInfo } from '@/decorators';
	import PageContainer from '@cnamts/vue-dot/src/elements/PageContainer';
	import { VCard, VDivider } from 'vuetify/lib';
	import RememberSection from '@/components/search-psychologist/remember-section/RememberSection.vue';
	import SearchSection from '@/components/search-psychologist/search-section/SearchSection.vue';
	import PsychologistMap from '@/components/search-psychologist/map-section/PsychologistMap.vue';
	import { SearchPsychologist } from '@/types/models/SearchPsychologist';
	import PsychologistList from '@/components/search-psychologist/result-section/PsychologistList.vue';
	import { City } from '@/types/models/City';
	import { TraceApiService } from '@/services/api/trace-api-service';
	import { eventCode } from '@/constants/trace-constant';

	@Component({
		components: {
			PsychologistList,
			PsychologistMap,
			SearchSection,
			RememberSection,
			PageContainer,
			VCard,
			VDivider
		}
	})
	export default class SearchPsychologistPage extends Vue {
		// Liste des psychologues
		psychologists: SearchPsychologist[] = [];

		// Id du psychologue sélectionné
		selectedPsychologistId: string | null = null;

		// Affichage de l'alerte "Pas encore de psychologues partenaires dans cette zone"
		displayNotFoundAlert = false;

		// Ville
		city: City | null = null;

		mounted(): void {
			TraceApiService.trace(eventCode.ACCES);
		}

		/**
		 * Met à jour l'id du psychologue sélectionné
		 * @param psychologistId Id du psychologue sélectionné
		 */
		handleChangePsychologistId(psychologistId: string): void {
			this.selectedPsychologistId = psychologistId;
		}

		/**
		 * Met à jour l'affichage de l'alerte "Pas encore de psychologues partenaires dans cette zone"
		 * @param isSearchDone Indique si la recherche est terminée
		 */
		handleSearchDone(isSearchDone: boolean): void {
			this.displayNotFoundAlert = isSearchDone && !this.psychologists.length;
		}

		/**
		 * Met à jour la liste des psychologues
		 * @param psychologistList Liste des psychologues
		 */
		handleChangePsychologistList(psychologistList: SearchPsychologist[]): void {
			this.displayNotFoundAlert = !psychologistList.length;
			this.psychologists = psychologistList;
			this.$nextTick(() => {
				this.scrollToPsychologistResult();
			});
		}

		/**
		 * Scroll jusqu'au résultat de la recherche
		 */
		scrollToPsychologistResult(): void {
			const psychologistContainer = document.getElementById('result-container');
			if (psychologistContainer) {
				psychologistContainer.scrollIntoView();
			}
		}

		/**
		 * Met à jour la ville
		 * @param city Ville
		 */
		handleChangeCity(city: City): void {
			this.city = city;
		}

		@Meta
		metaInfo(): MetaInfo {
			return {
				title: 'Recherche de psychologues',
				meta: [
					{
						name: 'Recherche de psychologues',
						content: 'Recherche de psychologues en fonction de la ville, du département ou du code postal'
					},
					{
						name: 'robots',
						content: 'noindex, nofollow'
					}
				]
			};
		}
	}
</script>

<style scoped>
.result-container {
	display: grid;
	grid-template-columns: 1fr 2fr;
	height: 800px;
	width: 90%;
	margin: auto;
}

@media screen and (max-width: 768px) {
	.result-container {
		grid-template-columns: 1fr;
		grid-template-rows: auto 2fr;
	}
}
</style>
